import { TextField } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import SessionFilter from '../Components/Global_Components/common/SessionFilter';
import BasicTable from '../Components/Global_Components/reacttable/table';
import { fetchPatientBill, getArchiveData } from '../Redux/Thunk/Actions';
import '../styles/Patients.scss';
import { parseBillHTML } from '../utils/billParser';
import { downloadExcel, printPDF } from '../utils/printUtils';

dayjs.extend(localizedFormat);

const InjectionReport = ({loaderchange, popupalert, popuptext}) => {
  const dispatch = useDispatch();
  const userdata = useSelector(state => state.counter.userdata);
  const sideactive = useSelector(state => state.counter.sidebarnav);

  // State management
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({pageIndex: 1, pageSize: 10});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [showArchive, setShowArchive] = useState(false);
  const [datacount, setdatacount] = useState(10);
  const [session, setSession] = useState('');
  const [isReportLoading, setIsReportLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  // Derived values
  const getFormattedDates = range => {
    const s = range && range[0] && dayjs(range[0]).isValid() ? dayjs(range[0]).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    const e = range && range[1] && dayjs(range[1]).isValid() ? dayjs(range[1]).format('YYYY-MM-DD') : s;
    return {startDate: s, endDate: e};
  };

  const {startDate, endDate} = getFormattedDates(dateRange);
  const isAdmin = userdata.role === 'Admin';

  const headers = {
    serialNo: 'S.NO',
    date: 'DATE',
    patientName: 'PATIENTS NAME',
    injection: 'INJECTION',
    billAmount: 'BILL AMOUNT',
    remarks: 'REMARKS'
  };

  const subHeaders = {
    billTotal: 'INJECTION BILL'
  };

  const fetchAllData = async (isArchive = false) => {
    const reqId = ++activeRequestIdRef.current;
    setIsReportLoading(true);
    loaderchange(true);

    const {startDate: fromDate, endDate: toDate} = getFormattedDates(dateRange);
    const action = isArchive ? getArchiveData : fetchPatientBill;
    const pageSize = 5000;
    const initialPayload = {
      page: 1,
      page_size: pageSize,
      search: searchQuery,
      from_date: fromDate,
      to_date: toDate,
      range: true,
      session: session
    };

    try {
      const initialResponse = await dispatch(action(initialPayload));

      if (reqId !== activeRequestIdRef.current) return;

      if (initialResponse?.payload?.status?.code === 200) {
        const {data, total_count} = initialResponse.payload;
        setdatacount(total_count || 0);
        const totalPages = Math.ceil((total_count || 0) / pageSize);

        const firstPageBills = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        let allBills = [...firstPageBills];

        if (totalPages > 1) {
          const maxPages = Math.min(totalPages, 3);
          for (let i = 2; i <= maxPages; i++) {
            const payload = {
              page: i,
              page_size: pageSize,
              search: searchQuery,
              from_date: fromDate,
              to_date: toDate,
              range: true,
              session: session
            };
            const res = await dispatch(action(payload));
            if (reqId !== activeRequestIdRef.current) return;
            const moreBills = Array.isArray(res?.payload?.data?.data) ? res.payload.data.data : Array.isArray(res?.payload?.data) ? res.payload.data : [];
            allBills.push(...moreBills);
          }
        }

        const injectionReports = allBills
          .flatMap(bill => {
            if (bill.report_template) {
              const parsed = parseBillHTML(bill.report_template);
              return (parsed.injection || []).map(item => ({
                ...item,
                patient_id: bill.patient_id,
                created_at: bill.created_at
              }));
            }
            return [];
          })
          .filter(item => item.description && item.amount !== undefined && item.amount !== null && item.amount !== '');

        if (reqId !== activeRequestIdRef.current) return;

        setTableData(injectionReports);
        setPagination(prev => ({...prev, pageIndex: 1}));

        if (injectionReports.length === 0) {
          popupalert(true);
          popuptext('No Data Available');
          setTimeout(() => popupalert(false), 2000);
        }
      } else {
        setTableData([]);
        setdatacount(0);
        setPagination(prev => ({...prev, pageIndex: 1}));
        popupalert(true);
        popuptext('No Data Available');
        setTimeout(() => popupalert(false), 2000);
      }
    } catch (error) {
      console.error('Error fetching injection report:', error);
      if (reqId === activeRequestIdRef.current) {
        setTableData([]);
        setdatacount(0);
        setPagination(prev => ({...prev, pageIndex: 1}));
        popupalert(true);
        popuptext('Error fetching report');
        setTimeout(() => popupalert(false), 2000);
      }
    } finally {
      if (reqId === activeRequestIdRef.current) {
        setIsReportLoading(false);
        loaderchange(false);
      }
    }
  };

  useEffect(() => {
    fetchAllData(showArchive);
  }, [showArchive]);

  const handleSearch = () => {
    if (isReportLoading) return;
    fetchAllData(showArchive);
  };

  const [billTotal] = useMemo(() => {
    let bill = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.amount) || 0);
    });
    return [bill];
  }, [tableData]);

  const currentPageData = useMemo(() => {
    const startIndex = (pagination.pageIndex - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, pagination]);

  const [currentPageTotal] = useMemo(() => {
    let total = 0;
    currentPageData.forEach(item => {
      total += Math.round(Number(item.amount) || 0);
    });
    return [total];
  }, [currentPageData]);

  const handlePageSizeChange = useCallback(pageSize => {
    setPagination(prev => {
      if (prev.pageSize === pageSize) return prev;
      return {...prev, pageSize, pageIndex: 1};
    });
  }, []);

  const handlePageIndexChange = useCallback(pageIndex => {
    setPagination(prev => {
      if (prev.pageIndex === pageIndex) return prev;
      return {...prev, pageIndex};
    });
  }, []);

  const handleDateChange = newDates => {
    setDateRange(newDates);
  };

  const handleSessionChange = useCallback(newSession => {
    setSession(newSession);
  }, []);

  const formattedData = tableData.map((item, index) => ({
    serialNo: index + 1,
    date: item.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : '--',
    patientName: `${item.patient_id?.titles || ''} ${item.patient_id?.first_name || ''} ${item.patient_id?.last_name || ''}`.trim(),
    injection: item.description,
    billAmount: Math.round(item.amount) || 0,
    remarks: item.remark
  }));

  const handlePrintPdf = () => {
    const subRow = [billTotal];
    printPDF({title: 'Injection Report', headers, data: formattedData, subHeaders, subRow});
  };
  const handleDownload = () => {
    const subRow = ['', '', '', '', billTotal, ''];
    downloadExcel({title: 'Injection Report', headers, data: formattedData, subRow});
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: 'SI No.',
        accessor: '',
        Cell: row => {
          return (
            <>
              <span>
                <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={String(1 + Number(row.row.id))}>
                  {1 + Number(row.row.id)}.
                </div>
              </span>
            </>
          );
        }
      },
      {
        Header: 'Date',
        accessor: 'created_at',
        Cell: ({value}) => <TooltipContent>{dayjs(value).format('DD-MM-YYYY')}</TooltipContent>,
        Footer: () => <span>TOTAL</span>
      },
      {
        Header: 'Patient Name',
        accessor: row => `${row.patient_id?.titles || ''} ${row.patient_id?.first_name || ''} ${row.patient_id?.last_name || ''}`.trim(),
        Cell: ({row}) => (
          <TooltipContent>
            {row.original.patient_id?.titles}.{row.original.patient_id?.first_name} {row.original.patient_id?.last_name}
          </TooltipContent>
        )
      },
      {
        Header: 'Injection',
        accessor: 'description',
        Cell: ({value}) => <TooltipContent>{value}</TooltipContent>
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageTotal && Math.round(currentPageTotal)}</span>
      },

      {
        Header: 'Remarks',
        accessor: 'remark',
        Cell: ({value}) => <TooltipContent>{value}</TooltipContent>
      }
    ],
    [showArchive, currentPageTotal]
  );

  return (
    <div className={`maincontainer-patient ${sideactive ? 'maincontainer-patient-sa' : ''}`}>
      <div className='patient-header'>
        <Breadcrumbs />
        <div className='add_btn' style={{display: 'flex', gap: '10px'}}>
          <button onClick={handlePrintPdf} className='btn_1' disabled={isReportLoading} style={{opacity: isReportLoading ? 0.7 : 1, cursor: isReportLoading ? 'not-allowed' : 'pointer'}}>
            Print
          </button>
          <button onClick={handleDownload} className='btn_1' disabled={isReportLoading} style={{opacity: isReportLoading ? 0.7 : 1, cursor: isReportLoading ? 'not-allowed' : 'pointer'}}>
            Download
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className='billableCard'>
          <p>
            {startDate === dayjs().format('YYYY-MM-DD') && endDate === dayjs().format('YYYY-MM-DD') ? "Today's Billing Overview" : 'Billing Overview'}
            &nbsp;:&nbsp;
            <span>{billTotal ?? 0}&nbsp;Rs</span>
          </p>
        </div>
      )}

      <div className='card'>
        <div className='card-body'>
          <div className='table-controls' style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
            <SearchInput value={searchQuery} onChange={setSearchQuery} onSearch={handleSearch} disabled={isReportLoading} />
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
              <SessionFilter value={session} onChange={handleSessionChange} disabled={isReportLoading} />
              <DateRangeFilter value={dateRange} onChange={handleDateChange} disabled={isReportLoading} />
              <button
                type='button'
                className='btn'
                style={{
                  backgroundColor: '#1b5a90',
                  color: '#ffffff',
                  height: '40px',
                  padding: '0 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isReportLoading ? 0.7 : 1,
                  cursor: isReportLoading ? 'not-allowed' : 'pointer'
                }}
                disabled={isReportLoading}
                onClick={handleSearch}
              >
                {isReportLoading ? (
                  <>
                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' style={{width: '14px', height: '14px', borderWidth: '2px'}}></span>
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <i className='bi bi-search' style={{fontSize: '13px'}}></i>
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <BasicTable
            columns={tableColumns}
            data={currentPageData}
            pagesize={handlePageSizeChange}
            pageindex={handlePageIndexChange}
            dtpagesize={pagination.pageSize}
            dtpageindex={pagination.pageIndex}
            datacount={tableData.length}
            disabled={isReportLoading}
            showAll={true}
          />
        </div>
      </div>

      <ReactTooltip id='table-tooltip' arrowColor='transparent' place='bottom-start' delayShow={200} className='custom-tooltip' />
    </div>
  );
};

// Sub-components for better readability
const TooltipContent = ({children}) => (
  <div data-tooltip-id='table-tooltip' data-tooltip-content={children}>
    {children}
  </div>
);

const Breadcrumbs = () => (
  <div className='row'>
    <div className='col'>
      <h3 className='page-title'>Injection Report</h3>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <a href='/'>Dashboard</a>
        </li>
        <li className='breadcrumb-item active'>Injection Report</li>
      </ul>
    </div>
  </div>
);

const SearchInput = ({value, onChange, onSearch, disabled}) => (
  <div className='input-group rounded' style={{width: '250px', height: '40px'}}>
    <input
      type='search'
      className='form-control rounded'
      aria-label='Search'
      aria-describedby='search-addon'
      value={value}
      disabled={disabled}
      style={{
        height: '40px'
      }}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onSearch && onSearch();
        }
      }}
      placeholder='Search...'
      maxLength={100}
    />
    <span 
      className='input-group-text border-0 bg-transparent p-0' 
      id='search-addon'
      style={{cursor: disabled ? 'not-allowed' : 'pointer'}}
      onClick={() => !disabled && onSearch && onSearch()}
    >
      <i
        className='bi bi-search'
        style={{
          color: '#1b5a90',
          width: '20px',
          height: '20px',
          marginLeft: '10px'
        }}></i>
    </span>
  </div>
);

const DateRangeFilter = ({value, onChange, disabled}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateRangePicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      inputFormat='DD/MM/YYYY'
      maxDate={dayjs()}
      renderInput={(startProps, endProps) => (
        <>
          <FormattedDateField {...startProps} disabled={disabled} />
          <FormattedDateField {...endProps} disabled={disabled} />
        </>
      )}
    />
  </LocalizationProvider>
);

const FormattedDateField = props => <TextField {...props} InputLabelProps={{shrink: true}} />;

export default InjectionReport;
